import React from "react";
import { motion } from "framer-motion";
import {
  CreditCard,
  DollarSign,
  Calendar,
  CheckCircle,
  AlertTriangle,
  XCircle,
  ChevronRight,
  TrendingUp,
  Shield,
  Clock,
  Eye,
} from "lucide-react";
import date from "date-and-time";
import BaseButton from "components/base-button/base-button";
import { formatMoney } from "../../utils/formatMoney";
import useModal from "hooks/use-modal";

const AccountCard = ({ account, offers }) => {
  const { openModal, modalTypes } = useModal();

  if (!account && !offers) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative group h-full"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-gray-500/10 to-gray-600/10 rounded-3xl blur-xl opacity-50" />
        <div className="relative bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-3xl p-6 h-full">
          <div className="animate-pulse">
            <div className="h-6 bg-white/10 rounded-lg w-3/4 mb-3" />
            <div className="h-4 bg-white/10 rounded-lg w-1/2 mb-6" />
            <div className="h-10 bg-white/10 rounded-xl mb-4" />
            <div className="h-12 bg-white/10 rounded-2xl" />
          </div>
        </div>
      </motion.div>
    );
  }

  // Determine account status and styling
  let accountStatus, statusIcon, statusColor, gradientColor, bgPattern;

  if (account.status === 1 || account.status === 2) {
    accountStatus = "closed";
    statusIcon = CheckCircle;
    statusColor = "text-green-400";
    gradientColor = "from-green-500 to-emerald-500";
    bgPattern = "bg-gradient-to-br from-green-500/5 to-emerald-500/5";
  } else if (account.status === 3) {
    accountStatus = "disputed";
    statusIcon = AlertTriangle;
    statusColor = "text-yellow-400";
    gradientColor = "from-yellow-500 to-orange-500";
    bgPattern = "bg-gradient-to-br from-yellow-500/5 to-orange-500/5";
  } else {
    accountStatus = "active";
    statusIcon = TrendingUp;
    statusColor = "text-blue-400";
    gradientColor = "from-blue-500 to-cyan-500";
    bgPattern = "bg-gradient-to-br from-blue-500/5 to-cyan-500/5";
  }

  const StatusIcon = statusIcon;
  const pattern = date.compile("MMM DD YYYY");
  const dateDueDate = account.due_date ? new Date(account.due_date) : null;

  const getActionButton = () => {
    if (account.status === 1 || account.status === 2) {
      return (
        <div className="flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-green-500/10 border border-green-500/20">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <span className="text-green-400 font-medium">Paid</span>
        </div>
      );
    } else if (account.status === 3) {
      return (
        <div className="flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-yellow-500/10 border border-yellow-500/20">
          <AlertTriangle className="w-5 h-5 text-yellow-400" />
          <span className="text-yellow-400 font-medium">Disputed</span>
        </div>
      );
    } else {
      return (
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => openModal(modalTypes.ModalOffer, { account, offers })}
          className={`w-full px-4 py-3 rounded-2xl bg-gradient-to-r ${gradientColor} text-white font-medium 
                     flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all`}
        >
          <Shield className="w-5 h-5" />
          <span>Resolve Account</span>
          <ChevronRight className="w-4 h-4" />
        </motion.button>
      );
    }
  };

  return (
    <motion.div
      whileHover={{ y: -2, transition: { duration: 0.2 } }}
      className="relative group h-full"
    >
      {/* Glow effect on hover */}
      <div
        className={`absolute inset-0 bg-gradient-to-r ${gradientColor} opacity-0 group-hover:opacity-10 
                    transition-opacity duration-300 rounded-3xl blur-xl`}
      />

      {/* Card content */}
      <div
        className={`relative ${bgPattern} backdrop-blur-md border border-white/10 rounded-3xl p-6 
                      hover:bg-white/[0.05] transition-all duration-300 h-full flex flex-col`}
      >
        {/* Status Badge */}
        <div className="flex items-center justify-between mb-4">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10`}
          >
            <StatusIcon className={`w-4 h-4 ${statusColor}`} />
            <span className={`text-xs font-medium ${statusColor} capitalize`}>
              {accountStatus}
            </span>
          </motion.div>

          {/* Info button */}
          <motion.button
            whileHover={{ scale: 1.1, rotate: 10 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => openModal(modalTypes.ModalCreditorInfo, { account })}
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
          >
            <Eye className="w-4 h-4 text-white/60" />
          </motion.button>
        </div>

        {/* Creditor Info */}
        <div className="mb-4">
          <div className="flex items-start gap-2">
            <CreditCard className="w-5 h-5 text-white/60 mt-1 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-white break-words">
                {account.chargeoff_creditor?.name || account.creditor.name}
              </h4>
              <p className="text-white/40 text-sm mt-1">
                #{account.chargeoff_creditor?.ref_id || account.creditor.ref_id}
              </p>
            </div>
          </div>
        </div>

        {/* Balance Section */}
        {(accountStatus === "active" || accountStatus === "pending") && (
          <>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex-grow"
            >
              <div className="bg-white/5 rounded-2xl p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white/60 text-sm">Current Balance</span>
                  <DollarSign className="w-4 h-4 text-white/40" />
                </div>
                <motion.p
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200 }}
                  className="text-2xl font-bold text-white"
                >
                  ${formatMoney(account.balance_current)}
                </motion.p>

                {/* Progress Bar */}
                {account.balance_start > 0 && (
                  <div className="mt-3">
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{
                          width: `${Math.min(((Number(account.balance_current) || 0) / account.balance_start) * 100, 100)}%`,
                        }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        className={`h-full bg-gradient-to-r ${gradientColor}`}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Due Date */}
              {dateDueDate && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="flex items-center gap-2 mb-4 px-4 py-3 bg-white/5 rounded-xl"
                >
                  <Calendar className="w-4 h-4 text-white/60" />
                  <div>
                    <p className="text-white/40 text-xs">Next Due Date</p>
                    <p className="text-white font-medium">
                      {date.format(dateDueDate, pattern)}
                    </p>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </>
        )}

        {/* Action Buttons */}
        <div className="space-y-3 mt-auto">
          {getActionButton()}

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => openModal(modalTypes.ModalCreditorInfo, { account })}
            className="w-full px-4 py-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 
                      text-white font-medium flex items-center justify-center gap-2 transition-all"
          >
            <Eye className="w-5 h-5" />
            <span>View Details</span>
          </motion.button>
        </div>

        {/* Decorative Elements */}
        <motion.div
          className="absolute -top-2 -right-2 w-20 h-20 bg-gradient-to-br opacity-10 rounded-full blur-2xl pointer-events-none"
          style={{
            background: `linear-gradient(135deg, ${gradientColor.split(" ")[1]} 0%, ${gradientColor.split(" ")[3]} 100%)`,
          }}
          animate={{
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>
    </motion.div>
  );
};

export default AccountCard;
